import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { toLearnAccountPath } from '@/app/accountSurface';

import { useAuthStore, useKidClassId } from './authStore';
import { isStudentManager } from './familyManager';
import { isExplicitParentStudio, isParentStudioPath } from './parentStudio';
import type { PrincipalKind } from './types';
import { useMe } from './useAuth';

interface ProtectedRouteProps {
  children: ReactNode;
  kind: PrincipalKind;
}

export function ProtectedRoute({ children, kind }: ProtectedRouteProps) {
  const location = useLocation();
  const accessToken = useAuthStore((s) => s.tokens[kind]);
  const bootstrapped = useAuthStore((s) => s.bootstrapped);
  const me = useMe(kind);
  // Portal studio intent selects the parent even while a kid is signed in.
  // Legacy parent-only studio links retain their existing behaviour.
  // Evaluated only for the kid gate and only on the allow-listed studio paths —
  // everything else keeps the exact behaviour below.
  const userToken = useAuthStore((s) => s.tokens.user);
  const userMe = useMe('user');
  const parentStudioBid =
    kind === 'kid' &&
    (isExplicitParentStudio(location.pathname, location.search) ||
      (!accessToken && !!userToken && isParentStudioPath(location.pathname)));
  // One account, either page (student-self-signup-prd.md §17, D-SSU-23): an
  // independent student who opens a Portal link (a receipt email, a Challenge
  // report link, a shared bookmark) with no parent session goes to the same
  // page in their Learn account instead of the parent sign-in.
  const kidToken = useAuthStore((s) => s.tokens.kid);
  const kidMe = useMe('kid');
  const kidClassId = useKidClassId();
  const studentAccountBid =
    kind === 'user' && !accessToken && !!kidToken && location.pathname.startsWith('/portal');

  // Wait for the one-shot /auth/refresh on app mount to settle. Without this,
  // a page reload always sees accessToken=null on the first render and would
  // immediately bounce the user to /login even when the HttpOnly refresh cookie
  // is valid.
  if (!bootstrapped) {
    return (
      <div className="flex h-full items-center justify-center text-slate-500">Loading session…</div>
    );
  }

  if (studentAccountBid && kidMe.isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-slate-500">Loading session…</div>
    );
  }

  if (studentAccountBid && isStudentManager(kidMe.data, kidClassId)) {
    const target = toLearnAccountPath(`${location.pathname}${location.search}${location.hash}`);
    return <Navigate to={target} replace />;
  }

  if (parentStudioBid) {
    if (userMe.isLoading) {
      return (
        <div className="flex h-full items-center justify-center text-slate-500">
          Loading session…
        </div>
      );
    }
    // A parent WITH a family gets the studio (the backend still authorizes every
    // project call — a cross-family project 403/404s into the studio's own
    // error UX). Any other user principal (teacher/admin, family-less) bounces
    // exactly like an anonymous visitor.
    if (userToken && !userMe.isError && userMe.data?.kind === 'user' && userMe.data.role === 'parent' && userMe.data.family_id) {
      return <>{children}</>;
    }
    return (
      <Navigate
        to={
          isExplicitParentStudio(location.pathname, location.search)
            ? '/portal/login'
            : '/learn/login'
        }
        state={{ from: location }}
        replace
      />
    );
  }

  if (!accessToken && !me.isLoading) {
    const fallback = kind === 'user' ? '/portal/login' : '/learn/login';
    return <Navigate to={fallback} state={{ from: location }} replace />;
  }

  if (me.isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-slate-500">Loading session…</div>
    );
  }

  if (me.isError || !me.data) {
    const fallback = kind === 'user' ? '/portal/login' : '/learn/login';
    return <Navigate to={fallback} state={{ from: location }} replace />;
  }

  // Each surface validates its own principal (`useMe(kind)`), so no cross-surface
  // bounce: a parent and a kid can both be signed in and each surface is reachable
  // as long as that kind's session exists.
  return <>{children}</>;
}
